import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { t } from '@/messages/zh-TW';

export interface ModalProps {
  uiId: string;
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * 原系統對話框外觀（§7 第 3 級，【雛形值待實機校準】）：
 *   遮罩 `.modal-backdrop { position:fixed; inset:0; background-color:#000 }`（bootstrap.css:6455-6463）
 *         `.modal-backdrop.in { opacity:.5 }` → bg-black/50
 *   層級 `.modal { z-index:2050 !important }`、`.modal-backdrop { z-index:2040 !important }`
 *        （css/style.css:3988-3993）── INSPINIA 把 bootstrap 的 1040/1050 整組往上推
 *   內容 `.modal-content { background:#fff; border-radius:4px; box-shadow:0 1px 3px rgba(0,0,0,.3) }`
 *        （css/style.css:3335-3342）
 *   標頭 `.modal-header { padding:15px; border-bottom:1px solid #e5e5e5 }`（bootstrap.css:6472-6476）
 *   標題 `.modal-title { font-size:1.10em }`（style-ePortfolio.css:238-240）
 *   內容 `.modal-body { padding:20px 30px 30px 30px }`（css/style.css:3347-3349）
 *   footer `.modal-footer { padding:15px; text-align:right; border-top:1px solid #e5e5e5 }`
 *        （bootstrap.css:6488-6492）
 *   關閉鈕 `.close { float:right; font-size:21px; font-weight:bold; opacity:.2 }`（bootstrap.css:6379-6388）
 */
const BORDER_COLOR_CLASS = 'border-[#e5e5e5]';

/**
 * 基礎對話框結構。
 *
 * ponytail 取捨（都不影響對外契約，之後要補都是內部改動）：
 *   - 沒有用 React portal，直接原地渲染。z-index 2050 已經蓋過所有東西，
 *     真的遇到 stacking context 被夾住時再改成 createPortal。
 *   - 沒有 focus trap、沒有開啟時自動聚焦。Escape 關閉有做（鍵盤逃生口不能沒有）。
 *   - 點遮罩不關閉。原系統的確認框用的是 jquery-confirm，多半也是要按按鈕；
 *     而且「點空白處就關掉」對填到一半的表單是資料破壞。
 *   - 尺寸只有一種（max-w-[600px]）。原系統的 .modal-lg／.modal-sm 到真的用到再開 variant。
 *
 * ModalFrame（標題＋內容＋確定取消按鈕的固定組合）是第二層的事，這裡只給結構。
 */
export function Modal({ uiId, open, title, onClose, children, footer }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const titleId = `${uiId}-title`;

  return (
    <div
      data-ui-id={uiId}
      className="fixed inset-0 z-[2050] flex items-start justify-center overflow-y-auto bg-black/50 p-[10px]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-ui-id={`${uiId}-dialog`}
        className="mt-[30px] w-full max-w-[600px] rounded-[4px] bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.3)]"
      >
        <div
          data-ui-id={`${uiId}-header`}
          className={`flex items-start justify-between border-b p-[15px] ${BORDER_COLOR_CLASS}`}
        >
          <h4 id={titleId} data-ui-id={titleId} className="text-[1.1em] font-bold text-ink">
            {title}
          </h4>
          <button
            type="button"
            data-ui-id={`${uiId}-close`}
            aria-label={t('action.close')}
            onClick={onClose}
            className="cursor-pointer text-[21px] leading-none font-bold text-black opacity-20 hover:opacity-50"
          >
            &times;
          </button>
        </div>
        <div data-ui-id={`${uiId}-body`} className="px-[30px] pt-[20px] pb-[30px] text-ink">
          {children}
        </div>
        {footer === undefined ? null : (
          <div
            data-ui-id={`${uiId}-footer`}
            className={`border-t p-[15px] text-right ${BORDER_COLOR_CLASS}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
